import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import * as awsui from '@amzn/awsui-design-tokens';

// Cloudscape components
import {
    Spinner,
    Flashbar,
    FlashbarProps
} from '@amzn/awsui-components-react';
import { I18nProvider } from '@amzn/awsui-components-react/polaris/i18n';

// Import all locales
import messages from '@amzn/awsui-components-react/polaris/i18n/messages/all.all';

// AWS Design System CSS
import '@amzn/awsui-global-styles/polaris.css';
import '@amzn/awsui-global-styles/dark-mode-utils.css';

// Employee Data
import { EmployeeDataProvider, UserRole } from '../common/EmployeeContext';
import { UserPermissions } from '../common/UserPermissions';
// Custom components
import Header from '../components/header/Header';
import PageContent from '../components/layouts/PageContent';
import CoachingForm from '../components/coaching/form/Form';
import CoachingDashboard from '../components/coaching/dashboard/Dashboard';
import CsqaForm from '../components/csqa/form/Form';
import CsqaDashboard from '../components/csqa/dashboard/Dashboard';
import ViewDetailsModal from '../components/csqa/dashboard/ViewDetails';

// Styles
import { Body } from '../components/layouts/styled';
import ThemeWrapper from '../style/ThemeWrapper';

// Settings
import settings from '../settings.json';

import { useContinuousAuth } from './App';

// Styled components
const BodyWrapper = styled.div`
    height: 100vh;
`;

const LoadingOverlay = styled.div`
    background-color: ${awsui.colorBackgroundContainerContent};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    flex-wrap: wrap;
    z-index: 100;
`;

const LoadingText = styled.div`
    margin-top: 10px;
    font-family: equipextralight;
`;

// Define types
type Permissions = string;

type EmployeeRole = {
    user: string;
    role?: UserRole;
    org: string;
};

export function EngagementSuiteV2() {
    return (
        <Body>
            <ThemeWrapper>
                <PageContent
                    expanded={true}
                    footer={true}
                    authenticated={true}
                >
                    <AuthedEngagementSuite />
                </PageContent>
            </ThemeWrapper>
        </Body>
    );
}

function AuthedEngagementSuite() {
    const [authorizedUser, setAuthorizedUser] = useState<boolean>(false);
    const [currentView, setCurrentView] = useState<string>('');
    const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
    const [loadingUserPermissions, setLoadingUserPermissions] = useState<boolean>(false);
    const [loadingRole, setLoadingRole] = useState<boolean>(false);
    const [user, setUser] = useState<string>('');
    const [org, setOrg] = useState<string>('');
    const [userPermissions, setUserPermissions] = useState<Set<string>>(new Set());
    const [role, setRole] = useState<UserRole>();

    const [evaluation, setEvaluation] = useState<any>(null);
    const [evaluationLoading, setEvaluationLoading] = useState<boolean>(false);

    const { getHeaders } = useContinuousAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Environment settings
    const settingEnv = useMemo(() => (settings as any)[window.location.host] || settings.default, []);
    const {
        api_prefix: apiPrefix,
        cstools: { enableBanner },
    } = settingEnv;

    // Allowed permissions
    const allowedPermissions = useMemo(() => new Set<string>([
        'createCsqa',
        'searchCsqa',
        'viewCsqa',
        'createCoaching',
        'searchCoaching',
        'viewCoaching'
    ]), []);

    //When the component mounts, check for query parameters
    useEffect(() => {
        const evaluationId = searchParams.get('id');
        const evaluationCreatedTimestamp = searchParams.get('createdTimestamp');
        if (evaluationId && evaluationCreatedTimestamp) {
            setEvaluationLoading(true);
            (async () => {
                try {
                    const headers = await getHeaders();
                    // Call your existing GET endpoint for CSQA. Adjust URL as needed.
                    const response = await axios.get(`${apiPrefix}/api/csqa-review`, {
                        params: { id: evaluationId, createdTimestamp: evaluationCreatedTimestamp },
                        headers,
                    });
                    setEvaluation(response.data);
                } catch (error) {
                    console.error('Error fetching evaluation details:', error);
                    setFlashbarItems(prev => [
                        ...prev,
                        { type: 'error', content: 'Unable to load evaluation details', dismissible: true, id: 'eval-fetch-error' },
                    ]);
                } finally {
                    setEvaluationLoading(false);
                }
            })();
        }
    }, [searchParams, apiPrefix, getHeaders]);

    useEffect(() => {

    }, [evaluation])

    const closeEvaluation = useCallback(() => {
        setEvaluation(null);
        // Optionally, remove the query parameters so that refreshing does not reopen the modal.
        setSearchParams({});
    }, [setSearchParams]);

    // Menu click handler
    const handleMenuSelection = useCallback((menuTitle: string) => {
        setCurrentView(menuTitle);
    }, []);

    // Fetch user permissions
    const getUserPermissions = useCallback(async () => {
        setLoadingUserPermissions(true);
        let permissionsSet = new Set<string>();
        try {
            const headers = await getHeaders();
            const { data } = await axios.get<Permissions[]>(`${apiPrefix}/api/permissions`, { headers });
            if (data) {
                permissionsSet = new Set(data);
                const hasAllowedPermission = [...permissionsSet].some(permission => allowedPermissions.has(permission));
                setAuthorizedUser(hasAllowedPermission);
                if (!hasAllowedPermission) {
                    navigate('/unauthorized');
                    return;
                } else {
                    handleMenuSelection('CSQA Dashboard');
                }
                if (permissionsSet.has(UserPermissions.AdminViewCoaching)) {
                    setRole(UserRole.Admin);
                }
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setFlashbarItems([{
                type: 'error',
                content: 'Failed to fetch user permissions.',
                dismissible: true,
                id: 'error-user-permissions'
            }]);
        } finally {
            setUserPermissions(permissionsSet);
            setLoadingUserPermissions(false);
        }
    }, [apiPrefix, allowedPermissions, getHeaders, navigate, handleMenuSelection]);

    // Assign user role
    const assignRole = useCallback(async () => {
        try {
            setLoadingRole(true);
            const headers = await getHeaders();
            const { data } = await axios.get<EmployeeRole>(`${apiPrefix}/api/agent-role`, { headers });
            setUser(data.user);
            if (data.role) {
                setRole(data.role);
            }
            if (data.org) {
                setOrg(data.org);
            }
        } catch (error: any) {
            console.error(error);
            setFlashbarItems([{
                type: 'error',
                content: 'There was a problem defining your role.',
                dismissible: true,
                id: 'error-role-definition'
            }]);
        } finally {
            setLoadingRole(false);
        }
    }, [apiPrefix, getHeaders]);

    // useEffect to fetch permissions on mount
    useEffect(() => {
        getUserPermissions();
    }, [getUserPermissions]);

    // useEffect to assign role when authorized
    useEffect(() => {
        if (authorizedUser) {
            assignRole();
        }
    }, [authorizedUser, assignRole]);

    const stableUserPermissions = useMemo(() => userPermissions, [userPermissions]);

    // Render
    return (
        <EmployeeDataProvider user={user} role={role} userPermissions={userPermissions}>
            <I18nProvider locale='...' messages={[messages]}>
                <BodyWrapper>
                    {(loadingUserPermissions || loadingRole) && (
                        <LoadingOverlay>
                            <Spinner size="large" />
                            <LoadingText>Loading User Permissions...</LoadingText>
                        </LoadingOverlay>
                    )}
                    {/* Flashbar for notifications */}
                    {flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
                    {/* Banner Alert */}
                    {/* {enableBanner &&
                <Alert
                    type='warning'
                    header='Please Advise'
                    dismissAriaLabel='Dismiss'
                >
                    This is a testing environment, submissions entered will not be accessible in production.
                </Alert>
            } */}
                    {/* HEADER COMPONENT */}
                    <Header
                        userPermissions={stableUserPermissions}
                        menuClick={handleMenuSelection}
                        currentView={currentView}
                        user={user}
                        org={org}
                        role={role}
                    />
                    {/* Conditional Rendering of Components */}
                    {evaluation && (
                        <ViewDetailsModal
                            loading={evaluationLoading}
                            visible={true}
                            onDismiss={closeEvaluation}
                            selectedReview={evaluation}
                            userPermissions={userPermissions}
                            viewOnly={true}
                        />
                    )}
                    {
                        currentView === 'Create Coaching' &&
                        <CoachingForm
                            menuClick={handleMenuSelection}
                            user={user}
                            role={role}
                            userPermissions={userPermissions}
                        />
                    }
                    {
                        currentView === 'Coaching Dashboard' &&
                        <CoachingDashboard
                            user={user}
                            role={role}
                            userPermissions={userPermissions}
                        />
                    }
                    {
                        currentView === 'Create CSQA' &&
                        <CsqaForm
                            menuClick={handleMenuSelection}
                            user={user}
                            role={role}
                            userPermissions={userPermissions}
                            org={org}
                        />
                    }
                    {
                        currentView === 'CSQA Dashboard' &&
                        <CsqaDashboard
                            user={user}
                            role={role}
                            userPermissions={userPermissions}
                            org={org}
                        />
                    }
                </BodyWrapper>
            </I18nProvider>
        </EmployeeDataProvider>
    );
}

export default AuthedEngagementSuite;
